import React, { Component } from 'react';
import { withRouter } from 'next/router';
import { WithRouterProps } from 'next/dist/client/with-router';

import cx from 'classnames';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import withBreakpoints, {
  InjectedProps as WithBreakpointsProps,
} from 'lib/withBreakpoints';
import { isContentHubOrArticlePage } from 'utils/determinePageVariant';
import getParentTopicsFromTopic from 'utils/getParentTopicsFromTopic';

import { LinkOrButton } from 'components/base';
import { Breadcrumbs } from 'styled';

import Language from 'constants/Language';
import { EmphasizeModuleColorScheme } from 'constants/ColorScheme';
import { RouteMap } from 'constants/Routes';
import { mobileTopBarHeight, tabletTopBarHeight } from 'constants/Sizing';

import { Article, OrganizedTopic, TopicLink } from 'types';

interface PassedProps {
  article: Article | null;
  organizedTopics: OrganizedTopic;
  notificationBarIsActive: boolean | null;
  notificationBarHeight: number;
}

type Props = WithRouterProps & PassedProps & WithBreakpointsProps;

interface State {
  hashHasChanged: boolean;
  containerTopValue: number;
}

const MAX_BREADCRUMBS = 3;

class BreadcrumbBar extends Component<Props, State> {
  state: State = {
    hashHasChanged: false,
    containerTopValue: 0,
  };

  componentDidMount() {
    window.addEventListener('hashchange', this.throttleHandleHashChange);
    this.setBreadcrumbsTopStyleValue();
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.throttleHandleHashChange);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.currentBreakpoint !== this.props.currentBreakpoint) {
      this.setBreadcrumbsTopStyleValue();
    }

    if (
      prevProps.notificationBarIsActive !==
        this.props.notificationBarIsActive ||
      prevProps.notificationBarHeight !== this.props.notificationBarHeight
    ) {
      this.setBreadcrumbsTopStyleValue();
    }
  }

  handleHashChange = () => {
    this.setState({ hashHasChanged: true });
    setTimeout(() => {
      this.setState({ hashHasChanged: false });
    }, 250);
  };

  setBreadcrumbsTopStyleValue = () => {
    const {
      currentBreakpoint,
      notificationBarIsActive,
      notificationBarHeight,
    } = this.props;

    const breakpointIsSmDown = ['EXTRA_SMALL', 'SMALL'].includes(
      currentBreakpoint
    );

    if (breakpointIsSmDown) {
      return this.setState({
        containerTopValue: notificationBarIsActive
          ? mobileTopBarHeight + notificationBarHeight
          : mobileTopBarHeight,
      });
    } else if (currentBreakpoint === 'MEDIUM') {
      return this.setState({
        containerTopValue: notificationBarIsActive
          ? tabletTopBarHeight + notificationBarHeight
          : tabletTopBarHeight,
      });
    }

    return this.setState({
      containerTopValue: notificationBarIsActive ? notificationBarHeight : 0,
    });
  };

  throttleHandleHashChange = throttle(this.handleHashChange, 250);

  render() {
    const {
      article,
      organizedTopics,
      router,
      router: { query },
    } = this.props;
    const { hashHasChanged, containerTopValue } = this.state;
    const contentHubSlugs = get(query, 'contentHubSlugs', []);
    const isContentHubLanding =
      router.route === RouteMap.CONTENT_HUB_LANDING.path;

    if (!isContentHubOrArticlePage(router) || !contentHubSlugs.length) {
      return null;
    }

    const slug = !!contentHubSlugs.length
      ? `/${contentHubSlugs.slice(-1)[0]}`
      : '/';
    const currentTopic: TopicLink | undefined = get(
      organizedTopics,
      `topicsBySlug[${slug}]`
    );
    const pageIsContentHubPage = !!currentTopic;
    const pageIsArticlePage = !pageIsContentHubPage && !isContentHubLanding;
    const paramsWithRoot = ['root'].concat(
      getParentTopicsFromTopic(
        organizedTopics,
        pageIsArticlePage ? get(article, 'meta.topic.slug', '') : slug
      )
    );

    const slicedParams = pageIsArticlePage
      ? paramsWithRoot.slice(-MAX_BREADCRUMBS)
      : paramsWithRoot.slice(-(MAX_BREADCRUMBS + 1));

    const slicedParamsAreInvalid =
      slicedParams.length === 2 && slicedParams[1] === '';

    if (slicedParamsAreInvalid) {
      return null;
    }

    const colorScheme = article
      ? EmphasizeModuleColorScheme[article.hero.color]
      : EmphasizeModuleColorScheme.WHITE;

    return (
      <div
        style={{
          top: `${containerTopValue}px`,
        }}
        className={cx(
          `BreadcrumbBar w100 px1_5 md:px2_25 bg-color-${colorScheme.background}`,
          {
            'BreadcrumbBar--article-nav':
              pageIsArticlePage && !pageIsContentHubPage,
            'BreadcrumbBar--content-hub-nav':
              pageIsContentHubPage && !pageIsArticlePage,
            sticky: !hashHasChanged,
            fixed: hashHasChanged,
          }
        )}
      >
        <div className="BreadcrumbBar__container h100 inline-flex justify-start sm:justify-between items-center events-all">
          <Breadcrumbs
            className="w100 h100 justify-end sm:justify-start sm:flex-grow sm:pr1"
            article={pageIsArticlePage ? article : null}
            organizedTopics={organizedTopics}
            paramsWithRoot={paramsWithRoot}
            slicedParams={slicedParams}
            showContentHubLandingListItem={
              paramsWithRoot.length < MAX_BREADCRUMBS
            }
            textColor={colorScheme.text}
          />
          <LinkOrButton
            ariaLabel={Language.t('Global.symptomCheckerAriaLabel')}
            containerClassName="none md:flex"
            label={Language.t('Global.symptomCheckerLabel')}
            to={RouteMap.SYMPTOM_CHECKER.path}
            variant="symptom-checker-white"
          />
        </div>
        {(!pageIsArticlePage ||
          (pageIsArticlePage && colorScheme.background === 'white')) && (
          <div className="border-top-gray-lighter w100" />
        )}
      </div>
    );
  }
}

export default withBreakpoints(withRouter(BreadcrumbBar));
